import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type CreateOrganisationBankAccountRequest,
  type OrganisationBankAccount,
  type UpdateOrganisationBankAccountRequest,
} from '../client';

/** Stable query-key factory for bank-account-scoped caches. */
export const organisationBankAccountKeys = {
  all: ['directory', 'organisation-bank-account'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'organisation-bank-account', 'org', organisationId] as const,
};

/**
 * Fetch every bank account for an organisation. Pass `null` to keep the
 * query disabled (e.g. while the org id is still resolving from Auth0).
 */
export function useOrganisationBankAccounts(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<OrganisationBankAccount[], Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<OrganisationBankAccount[], Error>({
    queryKey: organisationId
      ? organisationBankAccountKeys.byOrganisation(organisationId)
      : organisationBankAccountKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getOrganisationBankAccounts(organisationId);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around POST `/organisationBankAccounts`. Invalidates the
 * per-org list on success so the new row shows up.
 */
export function useCreateOrganisationBankAccount() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateOrganisationBankAccountRequest>({
    mutationKey: [...organisationBankAccountKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createOrganisationBankAccount(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({
        queryKey: organisationBankAccountKeys.byOrganisation(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/organisationBankAccounts`. The caller passes
 * `organisationId` alongside the body so we can target the exact list-cache
 * key (the wire body itself only carries the bank-account id).
 */
export function useUpdateOrganisationBankAccount() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; body: UpdateOrganisationBankAccountRequest }
  >({
    mutationKey: [...organisationBankAccountKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateOrganisationBankAccount(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationBankAccountKeys.byOrganisation(organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around DELETE `/organisationBankAccounts/{id}`. The
 * caller passes `organisationId` for the same cache-targeting reason.
 */
export function useDeleteOrganisationBankAccount() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { organisationId: string; id: string }>({
    mutationKey: [...organisationBankAccountKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteOrganisationBankAccount(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({
        queryKey: organisationBankAccountKeys.byOrganisation(organisationId),
      });
    },
  });
}
